import React from 'react';

const Fase789 = ({ 
    oralHealthData, 
    onOralHealthChange, 
    periodontalDisease, 
    onPeriodontalChange,
    malocclusion,
    onMalocclusionChange,
    fluorosis,
    onFluorosisChange,
    cpoCeoData,
    onCpoCeoChange,
    isViewOnly = false
}) => {
    
    const rows = [
        { id: 'row1', teeth: '16 17 55' },
        { id: 'row2', teeth: '11 21 51' },
        { id: 'row3', teeth: '26 27 65' },
        { id: 'row4', teeth: '36 37 75' },
        { id: 'row5', teeth: '31 41 71' },
        { id: 'row6', teeth: '46 47 85' }
    ];

    const handleOralHealthChange = (rowId, field, value) => {
        if (isViewOnly) return;

        if (value !== '') {
            const num = parseInt(value, 10);
            if (isNaN(num)) return;
            if (field === 'placa' || field === 'calculo') {
                if (num < 0 || num > 3) return;
            }
            if (field === 'gingivitis') {
                if (num < 0 || num > 1) return;
            }
            value = num.toString();
        }

        const currentData = oralHealthData || {};
        const currentRow = currentData[rowId] || { placa: '', calculo: '', gingivitis: '' };
        
        const newData = {
            ...currentData,
            [rowId]: { ...currentRow, [field]: value }
        };

        // Calculate sum automatically based on number of entered values (not always 6)
        let sum = 0;
        let count = 0;
        rows.forEach(row => {
            const rData = newData[row.id] || {};
            const val = parseFloat(rData[field]);
            if (!isNaN(val)) {
                sum += val;
                count++;
            }
        });

        const currentTotals = newData.totals || { placa: '', calculo: '', gingivitis: '' };
        newData.totals = { ...currentTotals, [field]: count > 0 ? (sum / count).toFixed(2).replace(/\.?0+$/, '') : '' };

        onOralHealthChange(newData);
    };

    const handleTotalChange = (field, value) => {
        if (isViewOnly) return;
        const currentData = oralHealthData || {};
        const currentTotals = currentData.totals || { placa: '', calculo: '', gingivitis: '' };
        onOralHealthChange({
            ...currentData,
            totals: { ...currentTotals, [field]: value }
        });
    };

    const handleCpoChange = (type, field, value) => {
        if (isViewOnly) return;
        
        // Ensure only numbers are allowed
        value = value.replace(/\D/g, '');
        
        const currentData = cpoCeoData || {};
        
        // Ensure we handle 'e' instead of 'p' for lowercase 'd'
        const currentSection = currentData[type] || (type === 'D' ? { c: '', p: '', o: '', total: '' } : { c: '', e: '', o: '', total: '' });
        
        const newSection = { ...currentSection, [field]: value };
        
        // Auto-calculate total
        if (field !== 'total') {
            const valC = parseInt(newSection.c) || 0;
            const valP_E = parseInt(newSection.p !== undefined ? newSection.p : newSection.e) || 0;
            const valO = parseInt(newSection.o) || 0;
            
            if (newSection.c !== '' || (newSection.p !== '' && newSection.p !== undefined) || (newSection.e !== '' && newSection.e !== undefined) || newSection.o !== '') {
                newSection.total = (valC + valP_E + valO).toString();
            } else {
                newSection.total = '';
            }
        }
        
        onCpoCeoChange({
            ...currentData,
            [type]: newSection
        });
    };

    const getOralVal = (rowId, field) => {
        if (rowId === 'totals') {
            return oralHealthData?.totals?.[field] || '';
        }
        return oralHealthData?.[rowId]?.[field] || '';
    };

    const getCpoVal = (type, field) => {
        return cpoCeoData?.[type]?.[field] || '';
    };

    const handleKeyDown = (e, rowIndex, colIndex) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextInput = document.getElementById(`oral-input-${Math.min(rowIndex + 1, 5)}-${colIndex}`);
            if (nextInput) { nextInput.focus(); nextInput.select(); }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const nextInput = document.getElementById(`oral-input-${Math.max(rowIndex - 1, 0)}-${colIndex}`);
            if (nextInput) { nextInput.focus(); nextInput.select(); }
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            if (colIndex < 2) {
                const nextInput = document.getElementById(`oral-input-${rowIndex}-${colIndex + 1}`);
                if (nextInput) { nextInput.focus(); nextInput.select(); }
            } else {
                document.getElementById(`periodontal-${Math.min(rowIndex, 2)}`)?.focus();
            }
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            if (colIndex > 0) {
                const nextInput = document.getElementById(`oral-input-${rowIndex}-${colIndex - 1}`);
                if (nextInput) { nextInput.focus(); nextInput.select(); }
            } else {
                document.getElementById(`tooth-cell-${rowIndex}-2`)?.focus();
            }
        }
    };

    const handleToothKeyDown = (e, rowIndex, partIndex) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            document.getElementById(`tooth-cell-${rowIndex}-${partIndex}`)?.click();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            document.getElementById(`tooth-cell-${Math.min(rowIndex + 1, 5)}-${partIndex}`)?.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            document.getElementById(`tooth-cell-${Math.max(rowIndex - 1, 0)}-${partIndex}`)?.focus();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            if (partIndex < 2) {
                document.getElementById(`tooth-cell-${rowIndex}-${partIndex + 1}`)?.focus();
            } else {
                const nextInput = document.getElementById(`oral-input-${rowIndex}-0`);
                if (nextInput) { nextInput.focus(); nextInput.select(); }
            }
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            if (partIndex > 0) {
                document.getElementById(`tooth-cell-${rowIndex}-${partIndex - 1}`)?.focus();
            }
        }
    };

    const handleCheckboxKeyDown = (e, group, index) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            document.getElementById(`${group}-${index}`)?.click();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            document.getElementById(`${group}-${Math.min(index + 1, 2)}`)?.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            document.getElementById(`${group}-${Math.max(index - 1, 0)}`)?.focus();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            if (group === 'periodontal') document.getElementById(`malocclusion-${index}`)?.focus();
            else if (group === 'malocclusion') document.getElementById(`fluorosis-${index}`)?.focus();
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            if (group === 'fluorosis') document.getElementById(`malocclusion-${index}`)?.focus();
            else if (group === 'malocclusion') document.getElementById(`periodontal-${index}`)?.focus();
            else if (group === 'periodontal') {
                const nextInput = document.getElementById(`oral-input-${index}-2`);
                if (nextInput) { nextInput.focus(); nextInput.select(); }
            }
        }
    };

    const styles = {
        tableBorder: { border: '1px solid #000', borderCollapse: 'collapse', textAlign: 'center' },
        headerGray: { backgroundColor: '#e6e6e6', padding: '4px', fontWeight: 'bold', fontSize: '9pt', border: '1px solid #000' },
        inputSmall: { width: '100%', border: 'none', textAlign: 'center', backgroundColor: 'transparent', outline: 'none', padding: '4px 0' },
        cellBox: { border: '1px solid #000', padding: 0 }
    };

    return (
        <div style={{ fontFamily: 'Calibri, sans-serif' }}>
            
            {/* ROW CONTAINER FOR FASES 7 AND 8 */}
            <table style={{ width: '100%', marginBottom: '0', borderCollapse: 'collapse', border: 'none' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '60%', verticalAlign: 'top', paddingRight: '10px', border: 'none' }}>
                            {/* 7. INDICADORES DE SALUD BUCAL */}
                            <table style={{ width: '100%', ...styles.tableBorder }}>
                        <tbody>
                            <tr>
                                <td colSpan="7" style={{ ...styles.headerGray, textAlign: 'left', fontSize: '10pt' }}>
                                    7 INDICADORES DE SALUD BUCAL
                                </td>
                            </tr>
                            <tr>
                                <td colSpan="4" style={{ ...styles.headerGray, textAlign: 'center', width: '130px' }}>HIGIENE ORAL SIMPLIFICADA</td>
                                <td style={{ ...styles.headerGray, textAlign: 'center' }}>ENFERMEDAD PERIODONTAL</td>
                                <td style={{ ...styles.headerGray, textAlign: 'center' }}>MAL OCLUSIÓN</td>
                                <td style={{ ...styles.headerGray, textAlign: 'center' }}>FLUOROSIS</td>
                            </tr>
                            <tr>
                                <td colSpan="4" style={{ ...styles.cellBox, verticalAlign: 'top', width: '130px' }}>
                                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                        <tbody>
                                            <tr>
                                                <td colSpan="3" style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', fontSize: '8pt', padding: '2px' }}>PIEZAS DENTALES</td>
                                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', fontSize: '5pt', fontWeight: 'bold', verticalAlign: 'top', padding: '2px', width: '15%', textAlign: 'center' }}>PLACA<br/>0-1-2-3</td>
                                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', fontSize: '5pt', fontWeight: 'bold', verticalAlign: 'top', padding: '2px', width: '15%', textAlign: 'center' }}>CÁLCULO<br/>0-1-2-3</td>
                                                <td style={{ borderBottom: '1px solid #000', fontSize: '5pt', fontWeight: 'bold', verticalAlign: 'top', padding: '2px', width: '15%', textAlign: 'center' }}>GINGIVITIS<br/>0 - 1</td>
                                            </tr>
                                            {rows.map((row, rowIndex) => {
                                                const parts = row.teeth.split(' ');
                                                return (
                                                    <tr key={row.id}>
                                                        {[0, 1, 2].map(partIndex => (
                                                            <td 
                                                                key={partIndex}
                                                                id={`tooth-cell-${rowIndex}-${partIndex}`}
                                                                tabIndex="0"
                                                                onKeyDown={(e) => handleToothKeyDown(e, rowIndex, partIndex)}
                                                                onClick={() => {
                                                                    if (isViewOnly) return;
                                                                    const currentData = oralHealthData || {};
                                                                    const currentRow = currentData[row.id] || { placa: '', calculo: '', gingivitis: '', painted: {} };
                                                                    const currentPainted = currentRow.painted || {};
                                                                    onOralHealthChange({
                                                                        ...currentData,
                                                                        [row.id]: {
                                                                            ...currentRow,
                                                                            painted: {
                                                                                ...currentPainted,
                                                                                [partIndex]: !currentPainted[partIndex]
                                                                            }
                                                                        }
                                                                    });
                                                                }}
                                                                style={{ 
                                                                    borderBottom: '1px solid #000', 
                                                                    borderRight: '1px solid #000', 
                                                                    padding: '4px', 
                                                                    fontSize: '8pt', 
                                                                    width: '18%', 
                                                                    height: '20px', 
                                                                    verticalAlign: 'middle',
                                                                    textAlign: 'center',
                                                                    cursor: isViewOnly ? 'default' : 'pointer',
                                                                    backgroundColor: oralHealthData?.[row.id]?.painted?.[partIndex] ? '#ef4444' : 'transparent',
                                                                    color: oralHealthData?.[row.id]?.painted?.[partIndex] ? 'white' : 'black',
                                                                    fontWeight: oralHealthData?.[row.id]?.painted?.[partIndex] ? 'bold' : 'normal'
                                                                }}
                                                            >
                                                                {parts[partIndex]}
                                                            </td>
                                                        ))}
                                                        
                                                        <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', padding: '4px', height: '20px', verticalAlign: 'top' }}>
                                                            <input id={`oral-input-${rowIndex}-0`} onKeyDown={(e) => handleKeyDown(e, rowIndex, 0)} type="text" maxLength="1" className="oral-input" style={styles.inputSmall} value={getOralVal(row.id, 'placa')} onChange={(e) => handleOralHealthChange(row.id, 'placa', e.target.value)} disabled={isViewOnly} />
                                                        </td>
                                                        <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', padding: '4px', height: '20px', verticalAlign: 'top' }}>
                                                            <input id={`oral-input-${rowIndex}-1`} onKeyDown={(e) => handleKeyDown(e, rowIndex, 1)} type="text" maxLength="1" className="oral-input" style={styles.inputSmall} value={getOralVal(row.id, 'calculo')} onChange={(e) => handleOralHealthChange(row.id, 'calculo', e.target.value)} disabled={isViewOnly} />
                                                        </td>
                                                        <td style={{ borderBottom: '1px solid #000', padding: '4px', height: '20px', verticalAlign: 'top' }}>
                                                            <input id={`oral-input-${rowIndex}-2`} onKeyDown={(e) => handleKeyDown(e, rowIndex, 2)} type="text" maxLength="1" className="oral-input" style={styles.inputSmall} value={getOralVal(row.id, 'gingivitis')} onChange={(e) => handleOralHealthChange(row.id, 'gingivitis', e.target.value)} disabled={isViewOnly} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            <tr>
                                                <td colSpan="3" style={{ borderRight: '1px solid #000', padding: '4px', fontSize: '8pt', fontWeight: 'bold', textAlign: 'right', height: '20px', verticalAlign: 'top' }}>TOTALES</td>
                                                <td style={{ borderRight: '1px solid #000', padding: '4px', height: '20px', verticalAlign: 'top' }}>
                                                    <input type="text" className="oral-input" style={styles.inputSmall} value={getOralVal('totals', 'placa')} readOnly disabled={isViewOnly} />
                                                </td>
                                                <td style={{ borderRight: '1px solid #000', padding: '4px', height: '20px', verticalAlign: 'top' }}>
                                                    <input type="text" className="oral-input" style={styles.inputSmall} value={getOralVal('totals', 'calculo')} readOnly disabled={isViewOnly} />
                                                </td>
                                                <td style={{ padding: '4px', height: '20px', verticalAlign: 'top' }}>
                                                    <input type="text" className="oral-input" style={styles.inputSmall} value={getOralVal('totals', 'gingivitis')} readOnly disabled={isViewOnly} />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>

                                {/* Periodontal Disease - Nested Table */}
                                <td style={{ ...styles.cellBox, verticalAlign: 'top', padding: 0, width: '100px' }}>
                                    <table style={{ borderCollapse: 'collapse', width: '100%', height: '100%' }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', padding: '5px', textAlign: 'left', fontSize: '8pt', width: '75px' }}>LEVE</td>
                                                <td style={{ borderBottom: '1px solid #000', padding: '5px', textAlign: 'center', width: '25px' }}><input id="periodontal-0" onKeyDown={(e) => handleCheckboxKeyDown(e, 'periodontal', 0)} type="checkbox" className="checkbox-inline" checked={periodontalDisease === 'LEVE'} onChange={() => !isViewOnly && onPeriodontalChange(periodontalDisease === 'LEVE' ? '' : 'LEVE')} disabled={isViewOnly} /></td>
                                            </tr>
                                            <tr>
                                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', padding: '5px', textAlign: 'left', fontSize: '8pt', width: '75px' }}>MODERADA</td>
                                                <td style={{ borderBottom: '1px solid #000', padding: '5px', textAlign: 'center', width: '25px' }}><input id="periodontal-1" onKeyDown={(e) => handleCheckboxKeyDown(e, 'periodontal', 1)} type="checkbox" className="checkbox-inline" checked={periodontalDisease === 'MODERADA'} onChange={() => !isViewOnly && onPeriodontalChange(periodontalDisease === 'MODERADA' ? '' : 'MODERADA')} disabled={isViewOnly} /></td>
                                            </tr>
                                            <tr>
                                                <td style={{ borderRight: '1px solid #000', padding: '5px', textAlign: 'left', fontSize: '8pt', width: '75px' }}>SEVERA</td>
                                                <td style={{ padding: '5px', textAlign: 'center', width: '25px' }}><input id="periodontal-2" onKeyDown={(e) => handleCheckboxKeyDown(e, 'periodontal', 2)} type="checkbox" className="checkbox-inline" checked={periodontalDisease === 'SEVERA'} onChange={() => !isViewOnly && onPeriodontalChange(periodontalDisease === 'SEVERA' ? '' : 'SEVERA')} disabled={isViewOnly} /></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>

                                {/* Malocclusion - Nested Table */}
                                <td style={{ ...styles.cellBox, verticalAlign: 'top', padding: 0, width: '90px' }}>
                                    <table style={{ borderCollapse: 'collapse', width: '100%', height: '100%' }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', padding: '5px', textAlign: 'left', fontSize: '8pt', width: '65px' }}>ANGLE I</td>
                                                <td style={{ borderBottom: '1px solid #000', padding: '5px', textAlign: 'center', width: '25px' }}><input id="malocclusion-0" onKeyDown={(e) => handleCheckboxKeyDown(e, 'malocclusion', 0)} type="checkbox" className="checkbox-inline" checked={malocclusion === 'ANGLE_I'} onChange={() => !isViewOnly && onMalocclusionChange(malocclusion === 'ANGLE_I' ? '' : 'ANGLE_I')} disabled={isViewOnly} /></td>
                                            </tr>
                                            <tr>
                                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', padding: '5px', textAlign: 'left', fontSize: '8pt', width: '65px' }}>ANGLE II</td>
                                                <td style={{ borderBottom: '1px solid #000', padding: '5px', textAlign: 'center', width: '25px' }}><input id="malocclusion-1" onKeyDown={(e) => handleCheckboxKeyDown(e, 'malocclusion', 1)} type="checkbox" className="checkbox-inline" checked={malocclusion === 'ANGLE_II'} onChange={() => !isViewOnly && onMalocclusionChange(malocclusion === 'ANGLE_II' ? '' : 'ANGLE_II')} disabled={isViewOnly} /></td>
                                            </tr>
                                            <tr>
                                                <td style={{ borderRight: '1px solid #000', padding: '5px', textAlign: 'left', fontSize: '8pt', width: '65px' }}>ANGLE III</td>
                                                <td style={{ padding: '5px', textAlign: 'center', width: '25px' }}><input id="malocclusion-2" onKeyDown={(e) => handleCheckboxKeyDown(e, 'malocclusion', 2)} type="checkbox" className="checkbox-inline" checked={malocclusion === 'ANGLE_III'} onChange={() => !isViewOnly && onMalocclusionChange(malocclusion === 'ANGLE_III' ? '' : 'ANGLE_III')} disabled={isViewOnly} /></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>

                                {/* Fluorosis - Nested Table */}
                                <td style={{ ...styles.cellBox, verticalAlign: 'top', padding: 0, width: '100px' }}>
                                    <table style={{ borderCollapse: 'collapse', width: '100%', height: '100%' }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', padding: '5px', textAlign: 'left', fontSize: '8pt', width: '75px' }}>LEVE</td>
                                                <td style={{ borderBottom: '1px solid #000', padding: '5px', textAlign: 'center', width: '25px' }}><input id="fluorosis-0" onKeyDown={(e) => handleCheckboxKeyDown(e, 'fluorosis', 0)} type="checkbox" className="checkbox-inline" checked={fluorosis === 'LEVE'} onChange={() => !isViewOnly && onFluorosisChange(fluorosis === 'LEVE' ? '' : 'LEVE')} disabled={isViewOnly} /></td>
                                            </tr>
                                            <tr>
                                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000', padding: '5px', textAlign: 'left', fontSize: '8pt', width: '75px' }}>MODERADA</td>
                                                <td style={{ borderBottom: '1px solid #000', padding: '5px', textAlign: 'center', width: '25px' }}><input id="fluorosis-1" onKeyDown={(e) => handleCheckboxKeyDown(e, 'fluorosis', 1)} type="checkbox" className="checkbox-inline" checked={fluorosis === 'MODERADA'} onChange={() => !isViewOnly && onFluorosisChange(fluorosis === 'MODERADA' ? '' : 'MODERADA')} disabled={isViewOnly} /></td>
                                            </tr>
                                            <tr>
                                                <td style={{ borderRight: '1px solid #000', padding: '5px', textAlign: 'left', fontSize: '8pt', width: '75px' }}>SEVERA</td>
                                                <td style={{ padding: '5px', textAlign: 'center', width: '25px' }}><input id="fluorosis-2" onKeyDown={(e) => handleCheckboxKeyDown(e, 'fluorosis', 2)} type="checkbox" className="checkbox-inline" checked={fluorosis === 'SEVERA'} onChange={() => !isViewOnly && onFluorosisChange(fluorosis === 'SEVERA' ? '' : 'SEVERA')} disabled={isViewOnly} /></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </td>

                {/* 8. ÍNDICES CPO-ceo */}
                <td style={{ width: '40%', verticalAlign: 'top', border: 'none' }}>
                    <table style={{ width: '100%', height: '100%', ...styles.tableBorder }}>
                        <tbody>
                            <tr>
                                <td colSpan="5" style={{ ...styles.headerGray, textAlign: 'left', fontSize: '10pt' }}>
                                    8 ÍNDICES CPO-ceo
                                </td>
                            </tr>
                            <tr style={{ fontSize: '9pt', fontWeight: 'bold' }}>
                                <td style={{ width: '20%' }}></td>
                                <td style={{ width: '20%', borderLeft: '1px solid #000', borderBottom: '1px solid #000' }}>C</td>
                                <td style={{ width: '20%', borderLeft: '1px solid #000', borderBottom: '1px solid #000' }}>P</td>
                                <td style={{ width: '20%', borderLeft: '1px solid #000', borderBottom: '1px solid #000' }}>O</td>
                                <td style={{ width: '20%', borderLeft: '1px solid #000', borderBottom: '1px solid #000' }}>TOTAL</td>
                            </tr>
                            <tr>
                                <td style={{ fontWeight: 'bold', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>D</td>
                                <td style={styles.cellBox}><input type="text" maxLength="2" style={styles.inputSmall} value={getCpoVal('D', 'c')} onChange={(e) => handleCpoChange('D', 'c', e.target.value)} disabled={isViewOnly} /></td>
                                <td style={styles.cellBox}><input type="text" maxLength="2" style={styles.inputSmall} value={getCpoVal('D', 'p')} onChange={(e) => handleCpoChange('D', 'p', e.target.value)} disabled={isViewOnly} /></td>
                                <td style={styles.cellBox}><input type="text" maxLength="2" style={styles.inputSmall} value={getCpoVal('D', 'o')} onChange={(e) => handleCpoChange('D', 'o', e.target.value)} disabled={isViewOnly} /></td>
                                <td style={styles.cellBox}><input type="text" maxLength="3" style={styles.inputSmall} value={getCpoVal('D', 'total')} readOnly disabled={isViewOnly} /></td>
                            </tr>
                            <tr style={{ fontSize: '9pt', fontWeight: 'bold' }}>
                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000' }}></td>
                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000' }}>c</td>
                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000' }}>e</td>
                                <td style={{ borderBottom: '1px solid #000', borderRight: '1px solid #000' }}>o</td>
                                <td style={{ borderBottom: '1px solid #000' }}>TOTAL</td>
                            </tr>
                            <tr>
                                <td style={{ fontWeight: 'bold', borderRight: '1px solid #000' }}>d</td>
                                <td style={{...styles.cellBox, borderBottom: 'none'}}><input type="text" maxLength="2" style={styles.inputSmall} value={getCpoVal('d', 'c')} onChange={(e) => handleCpoChange('d', 'c', e.target.value)} disabled={isViewOnly} /></td>
                                <td style={{...styles.cellBox, borderBottom: 'none'}}><input type="text" maxLength="2" style={styles.inputSmall} value={getCpoVal('d', 'e')} onChange={(e) => handleCpoChange('d', 'e', e.target.value)} disabled={isViewOnly} /></td>
                                <td style={{...styles.cellBox, borderBottom: 'none'}}><input type="text" maxLength="2" style={styles.inputSmall} value={getCpoVal('d', 'o')} onChange={(e) => handleCpoChange('d', 'o', e.target.value)} disabled={isViewOnly} /></td>
                                <td style={{...styles.cellBox, borderBottom: 'none'}}><input type="text" maxLength="3" style={styles.inputSmall} value={getCpoVal('d', 'total')} readOnly disabled={isViewOnly} /></td>
                            </tr>
                        </tbody>
                    </table>
                    <div style={{ width: '100%', marginBottom: '10px' }}>
                        <table style={{ width: '100%', ...styles.tableBorder, textAlign: 'left' }}>
                            <tbody>
                                <tr>
                                    <td colSpan="3" style={{ ...styles.headerGray, textAlign: 'left', fontSize: '9pt' }}>
                                        9 SIMBOLOGÍA DEL ODONTOGRAMA
                                    </td>
                                </tr>
                                <tr style={{ fontSize: '7pt' }}>
                                    <td style={{ padding: '2px', verticalAlign: 'top' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
                                            <span style={{ color: 'red', fontWeight: 'bold' }}>* rojo</span>
                                            <span>SELLANTE NEC.</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
                                            <span style={{ color: 'blue', fontWeight: 'bold' }}>* azul</span>
                                            <span>SELLANTE REAL.</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
                                            <span style={{ color: 'red', fontWeight: 'bold' }}>X rojo</span>
                                            <span>EXTRAC. IND.</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                            <span style={{ color: 'blue', fontWeight: 'bold' }}>X azul</span>
                                            <span>PÉRDIDA CARIES</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '2px', verticalAlign: 'top' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
                                            <span style={{ color: 'red', border: '1px solid red', borderRadius: '50%', width: '10px', height: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px' }}>X</span>
                                            <span>PÉRDIDA OTRA</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
                                            <span style={{ color: 'red', fontWeight: 'bold', border: '1px solid red', padding: '0 2px' }}>&Delta;</span>
                                            <span>ENDODONCIA</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '2px' }}>
                                            <span style={{ color: 'red', fontWeight: 'bold' }}>(&mdash;)</span>
                                            <span>PRÓTESIS REM.</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '2px', verticalAlign: 'top' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
                                            <span style={{ color: 'red', fontWeight: 'bold' }}>=</span>
                                            <span>PRÓTESIS TOTAL</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
                                            <span style={{ color: 'red', border: '1px solid red', width: '10px', height: '10px', display: 'inline-block' }}></span>
                                            <span>CORONA</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
                                            <span style={{ border: '2px solid blue', borderRadius: '50%', width: '10px', height: '10px', display: 'inline-block' }}></span>
                                            <span style={{ color: 'blue', fontWeight: 'bold' }}>azul</span>
                                            <span>OBTURADO</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                            <span style={{ border: '2px solid red', borderRadius: '50%', width: '10px', height: '10px', display: 'inline-block' }}></span>
                                            <span style={{ color: 'red', fontWeight: 'bold' }}>rojo</span>
                                            <span>CARIES</span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </td>
                    </tr>
                </tbody>
            </table>

        </div>
    );
};

export default Fase789;
